import React, { useMemo } from 'react';
import { Text } from '@hangar/web-components/core/Text';
import { Skeleton } from '@hangar/web-components/core/Skeleton';
import { useTranslation } from '../../hooks/i18n';
import { formatDateTimeWithTimezone } from '../../utils/date';
import * as S from './FlightChangesCases.styles';
import type { SortByField } from '../../services/api/flightChanges';

const SKELETON_COLUMNS: { sortBy: SortByField; labelKey: string }[] = [
  { sortBy: 'loadDate', labelKey: 'flightChangesCases.registration' },
  { sortBy: 'awb', labelKey: 'flightChangesCases.awb' },
  { sortBy: 'route', labelKey: 'flightChangesCases.route' },
  { sortBy: 'movementNumber', labelKey: 'flightChangesCases.movement' },
  { sortBy: 'notificationStatus', labelKey: 'flightChangesCases.notification' },
  { sortBy: 'analyst', labelKey: 'flightChangesCases.analyst' },
  { sortBy: 'status', labelKey: 'flightChangesCases.status' },
];

const TIMEZONE_LABEL_REFERENCE_DATE = '2025-01-01T12:00:00Z';

export const FlightChangesCasesSkeleton: React.FC = () => {
  const { t } = useTranslation();
  const registrationTimezoneLabel = useMemo(() => {
    const { timeZoneLabel } = formatDateTimeWithTimezone(TIMEZONE_LABEL_REFERENCE_DATE);
    return timeZoneLabel || t('flightChangesCases.localTime');
  }, [t]);

  return (
    <S.Container data-testid="flight-changes-skeleton">
      <Text variant="heading-lg" bold>
        {t('flightChangesCases.title')}
      </Text>
      <S.Card>
        <S.Header>
          <S.SubtitleTitle>
            <Text variant="heading-md" color="primary" bold={false}>
              {t('flightChangesCases.subtitle')}
            </Text>
          </S.SubtitleTitle>
          <S.RegistrationTimezoneText>
            <Text variant="body-md" color="secondary">
              {t('flightChangesCases.registrationTimezoneMessage', { timezone: registrationTimezoneLabel })}
            </Text>
          </S.RegistrationTimezoneText>
        </S.Header>
        <S.SkeletonFiltersRow>
          <S.SkeletonFilterFieldColumn key="awb">
            <div style={{ width: 72, maxWidth: '100%' }}>
              <Skeleton height="12px" />
            </div>
            <Skeleton height="40px" />
          </S.SkeletonFilterFieldColumn>
          <S.SkeletonFilterFieldColumn key="statuses">
            <div style={{ width: 72, maxWidth: '100%' }}>
              <Skeleton height="12px" />
            </div>
            <Skeleton height="40px" />
          </S.SkeletonFilterFieldColumn>
          <S.SkeletonFilterFieldColumn key="analyst">
            <div style={{ width: 72, maxWidth: '100%' }}>
              <Skeleton height="12px" />
            </div>
            <Skeleton height="40px" />
          </S.SkeletonFilterFieldColumn>
          <S.SkeletonFilterColumn key={4}>
            <div style={{ width: 72, maxWidth: '100%' }}>
              <Skeleton height="12px" />
            </div>
            <Skeleton height="40px" />
          </S.SkeletonFilterColumn>
        </S.SkeletonFiltersRow>
        <S.DataTableContainer>
          <S.DataTable aria-label={t('flightChangesCases.title')}>
            <S.DataTableHead>
              <S.DataTableRow>
                {SKELETON_COLUMNS.map(({ labelKey, sortBy }) => (
                  <S.DataTableHeaderCell key={sortBy}>
                    <Text variant="label-md" bold>
                      {t(labelKey)}
                    </Text>
                  </S.DataTableHeaderCell>
                ))}
                <S.DataTableHeaderCell style={{ width: 56 }} />
              </S.DataTableRow>
            </S.DataTableHead>
            <S.DataTableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <S.DataTableRow key={i}>
                  <S.DataTableCell
                    colSpan={SKELETON_COLUMNS.length + 1}
                    style={{ border: 'none', verticalAlign: 'middle' }}
                  >
                    <S.SkeletonRowCell>
                      <Skeleton />
                    </S.SkeletonRowCell>
                  </S.DataTableCell>
                </S.DataTableRow>
              ))}
            </S.DataTableBody>
          </S.DataTable>
        </S.DataTableContainer>
      </S.Card>
    </S.Container>
  );
};
